/**
 * GroceryStoresPanel — the minimal grocery Command Center panel: stores,
 * fridge cards (claimed / unclaimed with the household they belong to),
 * household counts, and issuing cards. Scoped to the chosen organization.
 * Grocery wording only.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
    Store, ShoppingBasket, Users, Plus, Loader2, AlertTriangle, Clock, Activity,
    TrendingUp, ShieldCheck, PenLine, Lock,
} from 'lucide-react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Slider } from '../../../components/ui/slider';
import GroceryHouseholdDrawer from './GroceryHouseholdDrawer';
import GroceryTreasurePanel from './GroceryTreasurePanel';

const SEVERITY = { high: 'text-rose-600 dark:text-rose-400', medium: 'text-amber-600 dark:text-amber-400',
    low: 'text-slate-600 dark:text-slate-300' };

/** One dense panel shell — the six panels all look the same on purpose. */
const Panel = ({ icon: Icon, title, note, testid, children, className = '' }) => (
    <section className={`rounded-2xl border border-slate-200 bg-white dark:border-slate-700/50 dark:bg-slate-900/40 ${className}`}
        data-testid={testid}>
        <header className="flex items-center gap-2 border-b border-slate-200 px-4 py-2.5 dark:border-slate-700/50">
            <Icon className="h-4 w-4 text-emerald-500" />
            <h3 className="text-sm font-bold text-foreground">{title}</h3>
            {note ? <span className="ml-auto text-[11px] text-muted-foreground">{note}</span> : null}
        </header>
        <div className="p-4">{children}</div>
    </section>
);

export default function GroceryStoresPanel({ organizationId }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [ids, setIds] = useState('');
    const [busy, setBusy] = useState(false);
    const [chef, setChef] = useState(null);
    const [failed, setFailed] = useState(false);
    const [warm, setWarm] = useState(null);
    const [warming, setWarming] = useState(false);
    const [pattern, setPattern] = useState(null);     // the grocer's door pattern
    const [patternBusy, setPatternBusy] = useState(false);

    /** THE PATTERN STRENGTH, TUNED LIVE. Read from the showroom's own play
     *  settings, saved back per mode, so the strength can be set on a live site
     *  without a new publish. */
    const loadPattern = useCallback(async () => {
        if (!organizationId || organizationId === 'all') { setPattern(null); return; }
        try {
            const r = await api.get('/play/settings', { params: { organization_id: organizationId } });
            setPattern({
                on: !!r.data.background_pattern,
                light: Math.round((r.data.background_pattern_opacity_light ?? 0.5) * 100),
                dark: Math.round((r.data.background_pattern_opacity_dark ?? 0.5) * 100),
            });
        } catch (_e) { setPattern(null); }
    }, [organizationId]);

    useEffect(() => { loadPattern(); }, [loadPattern]);

    const savePattern = async (next) => {
        setPattern(next);
        setPatternBusy(true);
        try {
            await api.put('/play/settings', {
                background_pattern_opacity_light: next.light / 100,
                background_pattern_opacity_dark: next.dark / 100,
            }, { params: { organization_id: organizationId } });
            toast.success(t('grocery.cc.patternSaved'));
        } catch (_e) {
            toast.error(t('grocery.couldNotLoad'));
            loadPattern();
        } finally { setPatternBusy(false); }
    };

    /** Safe to tap twice: the demo entry resumes, the chef answer comes from the
     *  cache and the store door only mints a session. */
    const warmUp = async () => {
        setWarming(true);
        try {
            const r = await api.post('/grocery/admin/demo/warmup',
                { organization_id: organizationId });
            setWarm(r.data);
            toast.success(t('grocery.cc.warmupDone', { total: r.data.total_seconds }));
        } catch (e) {
            toast.error(e?.response?.data?.detail === 'not_a_demo_organization'
                ? t('grocery.cc.warmupNotDemo') : t('grocery.couldNotLoad'));
        } finally { setWarming(false); }
    };
    const [openHousehold, setOpenHousehold] = useState(null);

    const load = useCallback(async () => {
        if (!organizationId || organizationId === 'all') { setData(null); return; }
        try {
            const r = await api.get('/grocery/admin/overview', { params: { organization_id: organizationId } });
            setData(r.data);
            setFailed(false);
        } catch (_e) { setData(null); setFailed(true); }
        // Ask your chef is an OPTIONAL ADD-ON. The grocer sees whether it is on
        // and what it has been used for this month — never a price.
        try {
            const [cfg, usage] = await Promise.all([
                api.get('/ask-chef/admin/settings', { params: { organization_id: organizationId } }),
                api.get('/ask-chef/admin/usage', { params: { organization_id: organizationId } }),
            ]);
            setChef({ ...cfg.data, usage: usage.data });
        } catch (_e) { setChef(null); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const issue = async () => {
        const list = ids.split(/[\s,\n]+/).filter(Boolean);
        if (!list.length) { toast.error(t('grocery.cc.needCardIds')); return; }
        setBusy(true);
        try {
            await api.post('/grocery/admin/cards/issue', {
                organization_id: organizationId,
                location_id: data?.stores?.[0]?.id || null,
                card_ids: list,
            });
            toast.success(t('grocery.cc.issued', { count: list.length }));
            setIds(''); load();
        } catch (_e) { toast.error(t('grocery.cc.issueFailed')); } finally { setBusy(false); }
    };

    if (!organizationId || organizationId === 'all') {
        return (
            <section className="rounded-2xl border border-slate-200 dark:border-slate-700/50 p-5 bg-white dark:bg-slate-900/40"
                data-testid="cc-band-grocery">
                <p className="text-sm text-muted-foreground">{t('grocery.cc.pickOrganization')}</p>
            </section>
        );
    }

    const tiles = [
        { key: 'activeHouseholds', icon: Users, value: data?.week?.active_households },
        { key: 'ordersThisWeek', icon: ShoppingBasket, value: data?.week?.orders_this_week },
        { key: 'itemsAdded', icon: Plus, value: data?.week?.items_added_this_week },
        { key: 'avgOrderSize', icon: TrendingUp, value: data?.week?.avg_order_size },
        { key: 'claimed', icon: ShoppingBasket, value: data?.totals?.claimed },
        { key: 'unclaimed', icon: ShoppingBasket, value: data?.totals?.unclaimed },
    ];
    const attention = data?.attention || [];
    // Two per-organization switches. The drawer is DEMO-ONLY until the Law 25
    // pieces exist; brand-named products are opt-in per grocer.
    const drawerEnabled = !!data?.settings?.household_detail_enabled;
    const brandsEnabled = !!data?.settings?.brands_enabled;

    const toggleBrands = async () => {
        setBusy(true);
        try {
            await api.post('/grocery/admin/catalog/brands-enabled', {
                organization_id: organizationId, enabled: !brandsEnabled,
            });
            toast.success(!brandsEnabled ? t('grocery.cc.brandsOn') : t('grocery.cc.brandsOff'));
            load();
        } catch (_e) { toast.error(t('common.error', 'Something went wrong')); } finally { setBusy(false); }
    };

    return (
        <section className="space-y-4" data-testid="cc-band-grocery">
            <h2 className="text-base md:text-lg font-bold text-foreground flex items-center gap-2"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
                <ShoppingBasket className="w-5 h-5 text-emerald-500" strokeWidth={1.75} />
                {t('grocery.cc.title')}
            </h2>

            {/* WARM THE SHOWROOM, so the first prospect does not pay for the
                cold start. Demo organisation only, safe to tap twice. */}
            {data?.settings?.is_demo_org ? (
                <div className="rounded-2xl border border-slate-200 p-4 dark:border-slate-700"
                    data-testid="grocery-cc-warmup">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-sm font-bold text-foreground">
                                {t('grocery.cc.warmupTitle')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {t('grocery.cc.warmupLine')}
                            </p>
                        </div>
                        <Button onClick={warmUp} disabled={warming} className="min-h-[44px] gap-2"
                            data-testid="grocery-cc-warmup-btn">
                            {warming ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            {t('grocery.cc.warmupBtn')}
                        </Button>
                    </div>
                    {warm ? (
                        <div className="mt-3 space-y-1" data-testid="grocery-cc-warmup-result">
                            {(warm.steps || []).map((s) => (
                                <p key={s.step} className="text-xs text-muted-foreground"
                                    data-testid={`grocery-cc-warmup-step-${s.step.replace(/ /g, '-')}`}>
                                    <span className={s.ok ? 'font-bold text-emerald-700 dark:text-emerald-300'
                                        : 'font-bold text-rose-600 dark:text-rose-400'}>
                                        {s.seconds}s
                                    </span>{' '}{s.step} — {s.detail}
                                </p>
                            ))}
                            <p className="text-xs font-semibold text-foreground"
                                data-testid="grocery-cc-warmup-when">
                                {t('grocery.cc.warmupWhen', {
                                    total: warm.total_seconds,
                                    when: new Date(warm.at).toLocaleString(),
                                })}
                            </p>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* THE GROCER'S DOOR PATTERN — strength per mode, tuned live. */}
            {data?.settings?.is_demo_org && pattern?.on ? (
                <div className="rounded-2xl border border-slate-200 p-4 dark:border-slate-700"
                    data-testid="grocery-cc-pattern">
                    <p className="text-sm font-bold text-foreground">
                        {t('grocery.cc.patternTitle')}
                    </p>
                    <p className="text-xs text-muted-foreground">{t('grocery.cc.patternLine')}</p>
                    {[['light', 'patternLight'], ['dark', 'patternDark']].map(([mode, key]) => (
                        <div key={mode} className="mt-4">
                            <div className="flex items-center justify-between gap-3">
                                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                                    htmlFor={`pattern-${mode}`}>
                                    {t(`grocery.cc.${key}`)}
                                </label>
                                <span className="text-sm font-bold text-foreground"
                                    data-testid={`grocery-cc-pattern-${mode}-value`}>
                                    {pattern[mode]}%
                                </span>
                            </div>
                            <Slider id={`pattern-${mode}`} className="mt-2"
                                data-testid={`grocery-cc-pattern-${mode}`}
                                value={[pattern[mode]]} min={0} max={100} step={5}
                                disabled={patternBusy}
                                onValueChange={([v]) => setPattern({ ...pattern, [mode]: v })}
                                onValueCommit={([v]) => savePattern({ ...pattern, [mode]: v })} />
                        </div>
                    ))}
                </div>
            ) : null}

            {/* AN EMPTY PANEL MUST NOT PASS FOR AN EMPTY STORE. */}
            {failed ? (
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/40 dark:bg-amber-500/10"
                    data-testid="grocery-cc-load-failed">
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                        {t('grocery.couldNotLoad')}
                    </p>
                    <Button variant="outline" onClick={load} className="min-h-[40px]"
                        data-testid="grocery-cc-load-retry">{t('grocery.tryAgain')}</Button>
                </div>
            ) : null}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {tiles.map(({ key, icon: Icon, value }) => (
                    <div key={key} className="rounded-2xl border border-slate-200 dark:border-slate-700/50 p-4 bg-white dark:bg-slate-900/40"
                        data-testid={`grocery-kpi-${key}`}>
                        <Icon className="w-4 h-4 text-muted-foreground mb-2" />
                        <p className="text-2xl font-bold text-foreground">{value ?? '—'}</p>
                        <p className="text-xs text-muted-foreground">{t(`grocery.cc.${key}`)}</p>
                    </div>
                ))}
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/40 p-4 space-y-3"
                data-testid="grocery-cc-catalog-settings">
                <p className="text-sm font-bold text-foreground">{t('grocery.cc.catalogSettings')}</p>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="max-w-xl">
                        <p className="text-sm text-foreground">{t('grocery.cc.brandRows')}</p>
                        <p className="text-xs text-muted-foreground">{t('grocery.cc.brandRowsWhy')}</p>
                    </div>
                    <Button variant={brandsEnabled ? 'default' : 'outline'} onClick={toggleBrands}
                        disabled={busy} className="min-h-[44px]"
                        data-testid="grocery-cc-brands-toggle">
                        {brandsEnabled ? t('grocery.cc.brandsStateOn') : t('grocery.cc.brandsStateOff')}
                    </Button>
                </div>
                {!drawerEnabled ? (
                    <p className="flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-xs text-muted-foreground dark:bg-slate-800/60"
                        data-testid="grocery-cc-drawer-gated">
                        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        {t('grocery.cc.drawerGated')}
                    </p>
                ) : null}
            </div>

            <GroceryTreasurePanel organizationId={organizationId} />

            {chef ? (                <div className="rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/40 p-4 space-y-2"
                    data-testid="grocery-cc-ask-chef">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-foreground">
                        <img src="/static/icons/grocery/chef-hat-avatar.svg" alt="" aria-hidden="true"
                            className="h-6 w-6" />
                        {t('askChef.cardTitle')}
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:bg-slate-800"
                            data-testid="grocery-cc-ask-chef-addon">{t('askChef.optionalAddon')}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                            chef.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                            data-testid="grocery-cc-ask-chef-state">
                            {chef.enabled ? t('grocery.cc.chefOn') : t('grocery.cc.chefOff')}
                        </span>
                    </p>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-5"
                        data-testid="grocery-cc-ask-chef-usage">
                        {[['questions', chef.usage?.questions_asked], ['free', chef.usage?.cache_hits_free],
                          ['calls', chef.usage?.model_calls],
                          ['suggestionsShown', chef.usage?.suggestions_shown],
                          ['sponsoredShown', chef.usage?.sponsored_shown]].map(([key, value]) => (
                            <div key={key}>
                                <p className="text-xl font-bold text-foreground">{value ?? '—'}</p>
                                <p className="text-xs text-muted-foreground">{t(`grocery.cc.chef_${key}`)}</p>
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                        {t('grocery.cc.chefModels', { chat: chef.chat_model, recipe: chef.recipe_model })}
                    </p>
                </div>
            ) : null}

            {/* ── NEEDS ATTENTION: a slim row when nothing is open ── */}
            {attention.length === 0 ? (
                <div className="flex h-12 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 dark:border-slate-700/50 dark:bg-slate-900/40"
                    data-testid="grocery-cc-attention">
                    <ShieldCheck className="h-4 w-4 text-emerald-500" />
                    <span className="truncate text-sm font-medium text-foreground"
                        data-testid="grocery-cc-attention-empty">{t('grocery.cc.allCaughtUp')}</span>
                </div>
            ) : (
                <Panel icon={AlertTriangle} title={t('grocery.cc.needsAttention')}
                    testid="grocery-cc-attention">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {attention.map((a) => (
                            <div key={a.id} data-testid={`grocery-cc-attention-${a.id}`}
                                className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-700/50">
                                <span className="truncate text-sm text-foreground">{t(`grocery.cc.att_${a.id}`)}</span>
                                <span className={`text-lg font-bold tabular-nums ${SEVERITY[a.severity]}`}>{a.count}</span>
                            </div>
                        ))}
                    </div>
                </Panel>
            )}

            {/* ── THE ORDER FUNNEL ── */}
            <Panel icon={ShoppingBasket} title={t('grocery.cc.orderFunnel')}
                note={t('grocery.cc.ordersTotal', { count: data?.week?.orders_total ?? 0 })}
                testid="grocery-cc-funnel">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
                    {(data?.funnel || []).map((f) => (
                        <div key={f.status} data-testid={`grocery-cc-funnel-${f.status}`}
                            className="rounded-xl border border-slate-200 px-2 py-2 text-center dark:border-slate-700/50">
                            <p className="text-xl font-bold tabular-nums text-foreground">{f.count}</p>
                            <p className="text-[11px] leading-tight text-muted-foreground">
                                {t(`grocery.cc.status_${f.status}`, f.status)}
                            </p>
                        </div>
                    ))}
                </div>
            </Panel>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* ── DELIVERY WINDOWS ── */}
                <Panel icon={Clock} title={t('grocery.cc.deliveryWindows')} testid="grocery-cc-windows">
                    <ul className="space-y-1.5">
                        {(data?.windows || []).map((w) => (
                            <li key={w.id} data-testid={`grocery-cc-window-${w.id}`}
                                className="flex items-center gap-2 text-sm">
                                <span className="w-28 shrink-0 text-muted-foreground">{w.day}</span>
                                <span className="text-foreground">{w.start}–{w.end}</span>
                                <span className="ml-auto tabular-nums text-foreground">{w.used}/{w.capacity}</span>
                                {w.closed ? (
                                    <span className="rounded bg-slate-200 px-1.5 text-[10px] font-bold uppercase text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                                        {t('grocery.cc.windowClosed')}
                                    </span>
                                ) : w.full ? (
                                    <span className="rounded bg-amber-100 px-1.5 text-[10px] font-bold uppercase text-amber-800">
                                        {t('grocery.cc.windowFull')}
                                    </span>
                                ) : null}
                            </li>
                        ))}
                        {!data?.windows?.length ? (
                            <li className="text-sm text-muted-foreground">{t('grocery.cc.noWindows')}</li>
                        ) : null}
                    </ul>
                </Panel>

                {/* ── TOP ITEMS, FROM ORDERS ONLY ── */}
                <Panel icon={TrendingUp} title={t('grocery.cc.topItems')}
                    note={t('grocery.cc.fromOrdersOnly')} testid="grocery-cc-top-items">
                    <ol className="space-y-1">
                        {(data?.top_items || []).map((i, n) => (
                            <li key={i.catalog_id || i.name} className="flex items-center gap-2 text-sm"
                                data-testid={`grocery-cc-top-${n}`}>
                                <span className="w-5 shrink-0 text-muted-foreground">{n + 1}</span>
                                <span className="truncate text-foreground">{i.name}</span>
                                <span className="ml-auto tabular-nums text-muted-foreground">{i.orders}</span>
                            </li>
                        ))}
                    </ol>
                    {/* TYPED REQUESTS: a phrase is named only once five households
                        typed it; everything rarer is counted, never shown. */}
                    <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-700/50"
                        data-testid="grocery-cc-custom-requests">
                        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                            <PenLine className="h-3.5 w-3.5" />{t('grocery.cc.customRequests')}
                        </p>
                        <ul className="mt-1 space-y-1">
                            {(data?.custom_requests?.named || []).map((c) => (
                                <li key={c.text} className="flex items-center gap-2 text-sm">
                                    <span className="truncate text-foreground">{c.text}</span>
                                    <span className="ml-auto tabular-nums text-muted-foreground">{c.households}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-1 text-xs text-muted-foreground" data-testid="grocery-cc-custom-other">
                            {t('grocery.cc.otherCustom', { count: data?.custom_requests?.other_count ?? 0,
                                floor: data?.custom_requests?.floor ?? 5 })}
                        </p>
                    </div>
                </Panel>
            </div>

            {/* ── LIVE ACTIVITY — shape only, never a typed word ── */}
            <Panel icon={Activity} title={t('grocery.cc.liveActivity')}
                note={t('grocery.cc.noTypedWords')} testid="grocery-cc-activity">
                <ul className="max-h-64 space-y-1 overflow-y-auto">
                    {(data?.activity || []).map((a, n) => (
                        <li key={`${a.at}-${n}`} className="flex items-center gap-2 text-xs"
                            data-testid={`grocery-cc-activity-${n}`}>
                            <span className="w-32 shrink-0 text-muted-foreground">
                                {a.at ? new Date(a.at).toLocaleString() : ''}
                            </span>
                            <span className="w-24 shrink-0 truncate font-semibold text-foreground">{a.household || '—'}</span>
                            <span className="text-muted-foreground">{t(`grocery.cc.ev_${a.kind}`, a.kind)}</span>
                            <span className="truncate text-foreground">
                                {a.custom ? t('grocery.cc.aTypedItem') : (a.item || '')}
                                {a.aisle ? ` · ${a.aisle}` : ''}
                            </span>
                        </li>
                    ))}
                    {!data?.activity?.length ? (
                        <li className="text-sm text-muted-foreground">{t('grocery.cc.noActivity')}</li>
                    ) : null}
                </ul>
            </Panel>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-900/40 overflow-hidden"
                data-testid="grocery-cc-fridge-cards">
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700/50 flex flex-wrap items-center gap-2">
                    <Input value={ids} onChange={(e) => setIds(e.target.value)}
                        placeholder={t('grocery.cc.issuePlaceholder')}
                        className="min-h-[44px] max-w-sm" data-testid="grocery-cc-issue-input" />
                    <Button onClick={issue} disabled={busy} className="min-h-[44px] gap-2" data-testid="grocery-cc-issue-btn">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {t('grocery.cc.issueCards')}
                    </Button>
                </div>
                <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-muted-foreground">
                        <tr className="border-b border-slate-200 dark:border-slate-700/50">
                            <th className="text-left px-4 py-2">{t('grocery.cc.card')}</th>
                            <th className="text-left px-4 py-2">{t('grocery.cc.state')}</th>
                            <th className="text-left px-4 py-2">{t('grocery.cc.household')}</th>
                            <th className="text-left px-4 py-2">{t('grocery.cc.unit')}</th>
                            <th className="text-right px-4 py-2">{t('grocery.cc.orders')}</th>
                            <th className="text-left px-4 py-2">{t('grocery.cc.lastOrder')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data?.cards || []).map((c) => (
                            <tr key={c.card_id} className="border-b border-slate-100 hover:bg-slate-50 dark:border-slate-800/60 dark:hover:bg-slate-900/60"
                                data-testid={`grocery-cc-card-${c.card_id}`}>
                                <td className="px-4 py-2.5 font-mono text-foreground">#{c.card_id}</td>
                                <td className="px-4 py-2.5">
                                    <span className={c.claimed ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
                                        {c.claimed ? t('grocery.cc.claimed') : t('grocery.cc.unclaimed')}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 text-foreground">
                                    {c.household_id && drawerEnabled ? (
                                        <button onClick={() => setOpenHousehold(c.household_id)}
                                            data-testid={`grocery-cc-open-household-${c.card_id}`}
                                            className="font-semibold text-emerald-700 underline-offset-2 hover:underline dark:text-emerald-300">
                                            {c.household_name}
                                        </button>
                                    ) : (c.household_name || '—')}
                                </td>
                                <td className="px-4 py-2.5 text-muted-foreground">{c.unit || '—'}</td>
                                <td className="px-4 py-2.5 text-right tabular-nums text-foreground">{c.orders ?? 0}</td>
                                <td className="px-4 py-2.5 text-muted-foreground">{c.last_order || '—'}</td>
                            </tr>
                        ))}
                        {!data?.cards?.length && (
                            <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                                {t('grocery.cc.noCards')}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {openHousehold ? (
                <GroceryHouseholdDrawer organizationId={organizationId} householdId={openHousehold}
                    onClose={() => setOpenHousehold(null)} />
            ) : null}
        </section>
    );
}
